import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle, Loader2, ArrowRight, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const plan = searchParams.get("plan");

  const [status, setStatus] = useState("checking"); // checking, confirmed, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(
          `${API}/payments/status/order/${orderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status === "paid") {
          setPaymentData(response.data);
          setStatus("confirmed");

          // Update local user data
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.subscription_plan = response.data.plan;
          user.subscription_status = "active";
          localStorage.setItem("user", JSON.stringify(user));
        } else if (pollCount < 12) {
          // Poll up to 12 times (1 minute total)
          setPollCount((prev) => prev + 1);
          setTimeout(checkStatus, 5000);
        } else {
          // Payment might still be pending - show success anyway
          setPaymentData(response.data);
          setStatus("confirmed");
        }
      } catch {
        if (pollCount < 3) {
          setPollCount((prev) => prev + 1);
          setTimeout(checkStatus, 3000);
        } else {
          setStatus("error");
        }
      }
    };

    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const planNames = {
    starter: "Base",
    pro: "Pro",
    institutional: "Institutional",
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <div className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-lg mx-auto text-center">
          {status === "checking" && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-[#00E676]/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-[#00E676] animate-spin" />
              </div>
              <h1 className="text-3xl font-bold">Confirming Payment...</h1>
              <p className="text-[#A1A1AA]">
                Please wait while we verify your payment. This may take a few
                moments.
              </p>
            </div>
          )}

          {status === "confirmed" && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-[#00E676]/20 flex items-center justify-center animate-pulse">
                <CheckCircle className="w-12 h-12 text-[#00E676]" />
              </div>
              <h1 className="text-3xl font-bold">Payment Successful! 🎉</h1>
              <p className="text-[#A1A1AA] text-lg">
                Welcome to the{" "}
                <span className="text-[#00E676] font-semibold">
                  {planNames[plan] || plan || "Premium"}
                </span>{" "}
                plan!
              </p>

              {paymentData && (
                <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 text-left space-y-4">
                  <div className="pb-4 border-b border-[#27272A] flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-white">Payment Receipt</h3>
                      <p className="text-xs text-[#A1A1AA]">Order #{orderId}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs border-[#27272A] hover:bg-[#27272A]"
                      onClick={() => window.print()}
                    >
                      Print Receipt
                    </Button>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Plan</span>
                      <span className="font-semibold text-white">
                        {planNames[paymentData.plan] || paymentData.plan}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Amount</span>
                      <span className="font-mono font-semibold text-white">
                        ${paymentData.amount}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Status</span>
                      <span className="text-[#00E676] font-semibold flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4" /> Confirmed
                      </span>
                    </div>
                    
                    {(paymentData.network || paymentData.token) && (
                      <div className="pt-3 mt-3 border-t border-[#27272A] space-y-3">
                        {paymentData.network && (
                          <div className="flex justify-between">
                            <span className="text-[#A1A1AA] text-sm">Network</span>
                            <span className="text-sm text-white">{paymentData.network}</span>
                          </div>
                        )}
                        {paymentData.token && (
                          <div className="flex justify-between">
                            <span className="text-[#A1A1AA] text-sm">Token</span>
                            <span className="text-sm font-medium text-white">{paymentData.token}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {paymentData.txHash && (
                      <div className="pt-3 mt-3 border-t border-[#27272A] flex justify-between items-center">
                        <span className="text-[#A1A1AA] text-sm">Tx Hash</span>
                        <a 
                          href={`https://etherscan.io/tx/${paymentData.txHash}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-mono text-xs text-[#00E676] hover:underline truncate max-w-[200px]"
                        >
                          {paymentData.txHash}
                        </a>
                      </div>
                    )}
                    
                    {paymentData.paidAt && (
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA] text-sm">Date</span>
                        <span className="text-sm text-[#A1A1AA]">
                          {new Date(paymentData.paidAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-[#00E676] hover:bg-[#00C853] text-black px-8 py-3 text-lg"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-400" />
              </div>
              <h1 className="text-3xl font-bold">Something Went Wrong</h1>
              <p className="text-[#A1A1AA]">
                We couldn't verify your payment. If you were charged, please
                contact support.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-[#1C1C1E] hover:bg-[#27272A] text-white"
                >
                  Back to Pricing
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#00E676] hover:bg-[#00C853] text-black"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
